import React from "react";
import PropTypes from "prop-types";

import clsx from "clsx";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { MuiPickersUtilsProvider, KeyboardDateTimePicker } from "@material-ui/pickers";

import { MIN_DESKTOP_WIDTH } from "../../../constants/ui";
import LocalizedUtils, { moment } from "../../../utils/LocalizedUtils";

import {
  defaultPasscodeTheme,
  textfield,
  switchControl,
  selectField,
  getSelectValues,
  getKeyFromSelectedValue,
  getSelectedValueTranslated,
} from "../../../constants/passCodeUtils";

import {
  CONFERENCE_TYPE,
  LANGUAGE,
  MUSICSOURCE,
  CALL_FLOW,
  CUSTOM_NAME,
  CONFERENCE_ID,
  SCHEDULED_DATE,
  SCHEDULED_DURATION,
  CONFERENCE_NAME,
  MAX_PARTICIPANTS,
  HOST_PASSCODE,
  GUEST_PASSCODE,
  CHAIR_DISCONNECT,
  OWNER_EMAIL,
  OWNER,
  COMMENT,
} from "../../../utils/responseValue";

const useStyles = makeStyles(theme => ({
  ...defaultPasscodeTheme(theme),
  paper: {
    display: "flex",
    padding: theme.spacing(3, 2),
    margin: theme.spacing(2, 0),
    flexDirection: "row",
  },
  generel: {
    padding: theme.spacing(3, 2),
    margin: theme.spacing(1, 0),
    display: "inline-block",
    flexGrow: 1,
  },
  billingData: {
    padding: theme.spacing(3, 2),
    margin: theme.spacing(1, 0),
    display: "inline-block",
    flexGrow: 2,
  },
  readOnly: {
    backgroundColor: "#ffffff",
    borderRadius: "5px",
  },
  timePicker: {
    paddingTop: theme.spacing(2),
  },
  centererButton: {
    width: "50%",
    textAlign: "center",
  },
}));

const PMConference = ({ confRestRep, setConfRestRep, selectValues, translate, requestNewPin }) => {
  const classes = useStyles();
  const isDesktop = useMediaQuery(`(min-width:${MIN_DESKTOP_WIDTH})`);

  const handleTextChange = name => event => {
    setConfRestRep({ ...confRestRep, [name]: event.target.value });
  };
  const handleSwitchChange = name => event => {
    setConfRestRep({ ...confRestRep, [name]: event.target.checked });
  };
  const handleSelectChange = name => event => {
    setConfRestRep({
      ...confRestRep,
      [name]: getKeyFromSelectedValue(selectValues[name], event.target.value),
    });
  };

  const handleDateChange = (name, date) => {
    setConfRestRep({
      ...confRestRep,
      [name]: moment.utc(date).valueOf(),
    });
  };

  const customStyle = theme => ({
    formControl: {
      width: "200px",
    },
  });

  return (
    <form className={classes.container} noValidate autoComplete="off">
      <div className={classes.centerer}>
        <TextField
          label={translate[OWNER]}
          className={
            clsx(classes.textField, {
              [classes.lineExpander]: isDesktop,
              [classes.textFieldMobile]: !isDesktop,
            }) + classes[OWNER]
          }
          value={confRestRep[OWNER]}
          onChange={handleTextChange(OWNER)}
          margin="normal"
        />
        <TextField
          label={translate[CONFERENCE_ID]}
          className={clsx(
            classes.textField,
            {
              [classes.lineExpander]: isDesktop,
              [classes.textFieldMobile]: !isDesktop,
            },
            classes.readOnly,
            classes[OWNER]
          )}
          value={confRestRep[CONFERENCE_ID] || ""}
          margin="normal"
          InputProps={{
            readOnly: true,
          }}
        />
      </div>
      <div className={classes.lineBreak} />
      <div className={classes.centerer}>
        {selectField(
          customStyle,
          translate[CONFERENCE_TYPE],
          getSelectValues(selectValues, CONFERENCE_TYPE),
          getSelectedValueTranslated(confRestRep[CONFERENCE_TYPE], selectValues, CONFERENCE_TYPE),
          handleSelectChange(CONFERENCE_TYPE)
        )}
        {selectField(
          customStyle,
          translate[LANGUAGE],
          getSelectValues(selectValues, LANGUAGE),
          getSelectedValueTranslated(confRestRep[LANGUAGE], selectValues, LANGUAGE),
          handleSelectChange(LANGUAGE)
        )}
        {selectField(
          customStyle,
          translate[MUSICSOURCE],
          getSelectValues(selectValues, MUSICSOURCE),
          getSelectedValueTranslated(confRestRep[MUSICSOURCE], selectValues, MUSICSOURCE),
          handleSelectChange(MUSICSOURCE)
        )}
        {selectField(
          customStyle,
          translate[CALL_FLOW],
          getSelectValues(selectValues, CALL_FLOW),
          getSelectedValueTranslated(confRestRep[CALL_FLOW], selectValues, CALL_FLOW),
          handleSelectChange(CALL_FLOW)
        )}
      </div>
      <div className={classes.lineBreak} />
      {textfield(classes, isDesktop, confRestRep[CUSTOM_NAME], translate[CUSTOM_NAME], handleTextChange(CUSTOM_NAME))}
      {textfield(classes, isDesktop, confRestRep[OWNER_EMAIL], translate[OWNER_EMAIL], handleTextChange(OWNER_EMAIL))}
      <div className={classes.lineBreak} />
      <div className={classes.timePicker}>
        <MuiPickersUtilsProvider libInstance={moment} utils={LocalizedUtils}>
          <KeyboardDateTimePicker
            label={translate[SCHEDULED_DATE]}
            className={clsx(classes.textField)}
            ampm={false}
            format="DD.MM.YYYY HH:mm:ss"
            value={confRestRep[SCHEDULED_DATE]}
            onChange={date => handleDateChange(SCHEDULED_DATE, date)}
          />
        </MuiPickersUtilsProvider>
      </div>
      {textfield(
        classes,
        isDesktop,
        confRestRep[SCHEDULED_DURATION],
        translate[SCHEDULED_DURATION],
        handleTextChange(SCHEDULED_DURATION)
      )}
      <div className={classes.lineBreak} />
      {textfield(
        classes,
        isDesktop,
        confRestRep[CONFERENCE_NAME],
        translate[CONFERENCE_NAME],
        handleTextChange(CONFERENCE_NAME)
      )}
      {textfield(
        classes,
        isDesktop,
        confRestRep[MAX_PARTICIPANTS],
        translate[MAX_PARTICIPANTS],
        handleTextChange(MAX_PARTICIPANTS)
      )}
      <div className={classes.lineBreak} />
      <TextField
        id="standard-name"
        label={translate[HOST_PASSCODE]}
        className={clsx(
          classes.textField,
          {
            [classes.lineExpander]: isDesktop,
            [classes.textFieldMobile]: !isDesktop,
          },
          classes.readOnly
        )}
        value={confRestRep[HOST_PASSCODE] || ""}
        margin="normal"
        InputProps={{
          readOnly: true,
        }}
      />
      <TextField
        id="standard-name"
        label={translate[GUEST_PASSCODE]}
        className={clsx(
          classes.textField,
          {
            [classes.lineExpander]: isDesktop,
            [classes.textFieldMobile]: !isDesktop,
          },
          classes.readOnly
        )}
        value={confRestRep[GUEST_PASSCODE] || ""}
        margin="normal"
        InputProps={{
          readOnly: true,
        }}
      />
      <div className={classes.lineBreak} />
      <div className={classes.centererButton}>
        <Button
          key="modPin-button"
          variant="contained"
          className={classes.button}
          onClick={() => requestNewPin(confRestRep.conferenceId, "MODERATOR_PIN", confRestRep)}
        >
          Generate new Host Passcode
        </Button>
      </div>
      <div className={classes.centererButton}>
        <Button
          key="guestPin-button"
          variant="contained"
          className={classes.button}
          onClick={() => requestNewPin(confRestRep.conferenceId, "PARTICIPANT_PIN", confRestRep)}
        >
          Generate new Guest Passcode
        </Button>
      </div>
      <div className={classes.lineBreak} />
      {switchControl(
        classes,
        confRestRep[CHAIR_DISCONNECT],
        CHAIR_DISCONNECT,
        translate[CHAIR_DISCONNECT],
        handleSwitchChange(CHAIR_DISCONNECT)
      )}
      <div className={classes.lineBreak} />
      <TextField
        id="meetyoo_comment"
        label={translate[COMMENT]}
        multiline
        defaultValue={confRestRep[COMMENT]}
        variant="outlined"
        onChange={handleTextChange(COMMENT)}
      />
      <div className={classes.lineBreak} />
    </form>
  );
};

PMConference.propTypes = {
  confRestRep: PropTypes.object.isRequired,
  selectValues: PropTypes.object.isRequired,
  setConfRestRep: PropTypes.func.isRequired,
  translate: PropTypes.object.isRequired,
  requestNewPin: PropTypes.func.isRequired,
};

export default PMConference;
